import { withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import {
    Condition,
    ConditionType,
    createFilterRow
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { getRow } from "@components/smart/smartTable/SmartTable.utils";
import { TId } from "@components/table";
import { TEntityKey } from "@evala/odata-metadata";
import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import { getNewItemsMaxId, setNestedValue } from "@odata/Data.utils";
import {
    DocumentAccountAssignmentSelectionEntity,
    EntityTypeName,
    IInternalDocumentEntity,
    InternalDocumentEntity,
    IPayingDocumentEntity,
    IPaymentDocumentItemEntity,
    PayingDocumentEntity
} from "@odata/GeneratedEntityTypes";
import { CbaEntryTypeCode, ClearedStatusCode, DocumentTypeCode, SelectionCode } from "@odata/GeneratedEnums";
import { SAVED_VATS_PATH } from "@pages/admin/vatRules/VatRules.global.utils";
import { getCompanyCurrency, isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import { isObjectEmpty, roundToDecimalPlaces } from "@utils/general";
import i18next from "i18next";
import React, { ReactElement } from "react";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import BindingContext, { createPath, IEntity } from "../../../odata/BindingContext";
import { getUtcDate } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import {
    docOnlyAmountPaidFormatter, getCorrectExchangeRate,
    IBankCustomData,
    IPairTableViewBaseProps,
    IPaymentDocumentItemEntityExpanded
} from "../../banks/bankTransactions/BankTransactions.utils";
import { AmountDialog } from "../../banks/PairTableAmountDialog";
import {
    PairTableViewBaseForExtend
} from "../../banks/PairTableViewBase";
import { DocumentStatusLocalPath, getStatusFilterId } from "../../reports/CommonDefs";

class InternalDocumentPairTableView extends PairTableViewBaseForExtend {
    constructor(props: IPairTableViewBaseProps) {
        super(props);

        this.presetDefaultFilters = this.presetDefaultFilters.bind(this);

        this.presetDefaultFilters();
    }

    get isCba(): boolean {
        return isCashBasisAccountingCompany(this.props.storage.context);
    }

    getAmountPaidFormatterFn = () => {
        return docOnlyAmountPaidFormatter;
    };

    setOriginItems(): void {
        const { rootStorage, storage } = this.props;
        const entity: IInternalDocumentEntity = rootStorage.data.entity;
        const pairedDocuments: Record<TEntityKey, IPaymentDocumentItemEntityExpanded> = {};

        for (const item of (entity.Items || [])) {
            if (item.LinkedDocument?.Id) {
                const key = item.LinkedDocument.Id;

                pairedDocuments[key] = {
                    ...pairedDocuments[key],
                    Description: item.Description,
                    LinkedDocument: item.LinkedDocument,
                    Amount: item.Amount,
                    Id: item.Id,
                    TransactionAmount: item.TransactionAmount
                };

                if (!this.isCba) {
                    pairedDocuments[key].AccountAssignmentSelection = item.AccountAssignmentSelection;
                }
            }
        }

        this.origPairedDocuments = { ...pairedDocuments };

        storage.setCustomData({
            rootStorage,
            pairedDocuments
        });

        (rootStorage as FormStorage<unknown, IBankCustomData>).setCustomData({
            pairedStorage: storage
        });
    }

    savePairChanges = async (storage: FormStorage, pairedDocs: Record<TEntityKey, IPaymentDocumentItemEntityExpanded>): Promise<void> => {
        const entity = storage.data.entity as IInternalDocumentEntity;

        if (!entity.Items) {
            entity.Items = [];
        }

        const items = entity.Items.filter((item: IEntity) => !item.LinkedDocument?.Id);
        const newItems = [];

        let maxId = getNewItemsMaxId(entity.Items) + 1;

        for (const [key, link] of Object.entries(pairedDocs)) {
            let item = entity.Items.find((item) => item.LinkedDocument?.Id?.toString() === key);
            let isNew = false;

            if (!item) {
                isNew = true;
                item = BindingContext.createNewEntity(maxId++, link);
            }

            item.Description = item.Description ?? this.props.rootStorage.t("InternalDocument:Form.DocumentClearing");
            item.Amount = link.Amount;
            item.TransactionAmount = link.TransactionAmount;
            item.TransactionCurrencyCode = this.isCba ? getCompanyCurrency(storage.context) : link.LinkedDocument.TransactionCurrencyCode;

            item.LinkedDocument = {
                ...item.LinkedDocument,
                ExchangeRatePerUnit: link.LinkedDocument.ExchangeRatePerUnit
            };

            delete (item as IPaymentDocumentItemEntity).ExchangeRate;

            if (!this.isCba && isObjectEmpty(item.AccountAssignmentSelection)) {
                item.AccountAssignmentSelection = {
                    Selection: {
                        Code: SelectionCode.Default
                    },
                    AccountAssignment: {
                        ...this.props.rootStorage.getValueByPath(createPath(InternalDocumentEntity.AccountAssignmentSelection, DocumentAccountAssignmentSelectionEntity.AccountAssignment)),
                        Id: SelectionCode.Default as unknown as number
                    }
                };
            }

            if (isObjectEmpty(item.LabelSelection)) {
                item.LabelSelection = {
                    SelectionCode: SelectionCode.Default
                };
            }

            setNestedValue(SelectionCode.Default, SAVED_VATS_PATH, item);
            isNew ? newItems.push(item) : items.push(item);
        }

        storage.data.entity.Items = [...items, ...newItems];
        storage.handleFirstChange();
    };

    presetDefaultFilters() {
        const { storage, rootStorage } = this.props;

        storage.setFilterValueByPath(DocumentStatusLocalPath, [
            createFilterRow({
                type: ConditionType.Excluded,
                value: [
                    getStatusFilterId(EntityTypeName.ClearedStatus, ClearedStatusCode.Cleared)
                ]
            })
        ]);

        if (this.isCba) {
            const amountToPayPath = rootStorage.getValueByPath(InternalDocumentEntity.CbaEntryTypeCode) === CbaEntryTypeCode.Income ? PayingDocumentEntity.TransactionAmountToReceive : PayingDocumentEntity.TransactionAmountToPay;
            storage.setFilterValueByPath(amountToPayPath, [createFilterRow({
                value: 0,
                condition: Condition.GreaterThan
            })]);
        }

        storage.setFilterValueByPath(PayingDocumentEntity.DocumentType, [createFilterRow({
            value: [DocumentTypeCode.InvoiceReceived, DocumentTypeCode.InvoiceIssued,
                DocumentTypeCode.OtherLiability, DocumentTypeCode.OtherReceivable,
                DocumentTypeCode.ProformaInvoiceIssued, DocumentTypeCode.ProformaInvoiceReceived,
                DocumentTypeCode.CorrectiveInvoiceIssued, DocumentTypeCode.CorrectiveInvoiceReceived]
        })
        ]);

        storage.applyFilters();
    }

    canRowBePaired = (rowId: TId) => {
        const row = getRow(this.props.storage.tableAPI.getState().rows, rowId);
        const isPaired = !!this.origPairedDocuments?.[(rowId as BindingContext)?.getKey?.()];

        if (!isPaired && row?.customData) {
            const entity: IPayingDocumentEntity = row.customData.entity as IPayingDocumentEntity;
            return entity.ClearedStatusCode !== ClearedStatusCode.Cleared;
        }

        return true;
    };

    get secondaryTitle(): string {
        return i18next.t("InternalDocument:Form.ClearedDocument");
    }

    get successSubtitle(): string {
        return i18next.t("InternalDocument:Form.Paired");
    }

    get addMenuConfiguration() {
        return [
            { id: "InvoicesReceived", permission: UserPermissionCodes.InvoicesReceived },
            { id: "InvoicesIssued", permission: UserPermissionCodes.InvoicesIssued },
            { id: "OtherLiabilities", permission: UserPermissionCodes.Reports },
            { id: "OtherReceivables", permission: UserPermissionCodes.InvoicesReceived },
            { id: "CorrectiveInvoicesReceived", permission: UserPermissionCodes.InvoicesReceived },
            { id: "CorrectiveInvoicesIssued", permission: UserPermissionCodes.InvoicesIssued }
        ];
    }

    processRowPairing = async (rowId: TId): Promise<Record<TEntityKey, IPaymentDocumentItemEntityExpanded>> => {
        const { storage, rootStorage } = this.props;
        const bc = rowId as BindingContext;
        const key = bc.getKey();
        const pairedDoc = this.getPairedDocuments();

        if (pairedDoc[key]) {
            delete pairedDoc[key];
        } else {
            const type = rootStorage.oData.metadata.getTypeForPath(this.getRowEntitySet(bc));
            const row = getRow(storage.tableAPI.getState().rows, bc);
            const document = row?.customData?.entity as IPayingDocumentEntity;
            const numberOurs = document.NumberOurs;
            const isCbaCompany = isCashBasisAccountingCompany(this.context);
            const internalDocumentEntity: IInternalDocumentEntity = rootStorage.data.entity;

            const pairedItem = internalDocumentEntity.Items.find((item) => item.LinkedDocument?.Id === parseInt(key.toString()));
            const isItemNew = !pairedItem?.Id && !!pairedItem?.[BindingContext.NEW_ENTITY_ID_PROP];

            let clearedAmount = 0;
            if (!isItemNew) {
                clearedAmount = pairedItem?.TransactionAmount ?? 0;
            }

            if (isCbaCompany) {
                this.fetchDocumentCategories(document);
            }

            const docCurrency = document?.TransactionCurrency?.Code;

            const transactionAmount = document.TransactionAmountDue + clearedAmount;

            this.props.setBusy(true);

            const docExchangeRate = await getCorrectExchangeRate({
                id: document?.Id,
                date: (isCbaCompany ? internalDocumentEntity.DateCbaEntry : internalDocumentEntity.DateAccountingTransaction) ?? getUtcDate(),
                currency: docCurrency
            }, storage.context);

            if (!docExchangeRate) {
                this.props.setBusy?.(false);
                // if exchange rate is not found, we show dialog to user with information,
                // there is no exchange rate for the date. Row cannot be paired.
                await this.props.confirmationDialog.open({
                    content: storage.t("Common:Errors.NoExchangeRateForGivenDate"),
                    allowCancel: false
                });
                return null;
            }

            const amount = roundToDecimalPlaces(2, transactionAmount * docExchangeRate);

            pairedDoc[key] = {
                Amount: amount,
                TransactionAmount: transactionAmount,
                LinkedDocument: {
                    TransactionAmount: document?.TransactionAmount,
                    ExchangeRatePerUnit: docExchangeRate,
                    TransactionCurrencyCode: docCurrency,
                    TransactionCurrency: {
                        Code: docCurrency
                    },
                    TransactionAmountDue: document?.TransactionAmountDue,
                    Id: parseInt(key as string),
                    NumberOurs: numberOurs as string,
                    DocumentTypeCode: document?.DocumentType.Code,
                    _metadata: {
                        "": { type: `#${type.getFullName()}` }
                    }
                }
            };
        }

        return pairedDoc;
    }

    renderAmountDialog = (): ReactElement => {
        return <AmountDialog
            needConfirmDialog={this.needConfirmDialog}
            rootStorage={this.props.rootStorage}
            clearingByID={true}
            onClose={() => {
                this.needConfirmDialog = null;
                this.forceUpdate();
                this.rerenderTable();
            }}
            storage={this.props.storage}/>;
    };
}

export default withPermissionContext(withBusyIndicator({
    isDelayed: true,
    passBusyIndicator: true
})(withConfirmationDialog(InternalDocumentPairTableView)));